import { useCallback, useMemo } from 'react';
import { MathUtils } from 'three';

import { clampAngle180 } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  selectDimensionItemIds,
  selectTemplatesByIds,
} from '@/modules/floorplanDesign/selectors/selectedEntitiesSelectors';

import { createItems, toItemUpdates, updateItemsField } from './items';
import { updateTemplatesField } from './templates';
import { useMoveWithTemplates } from './useMoveWithTemplates';
import { DimensionField, DimensionKind, EditMode, SelectionDimensionsProps } from './types';

type Props = Pick<SelectionDimensionsProps, 'selectedIds'> & {
  appMode: ReturnType<typeof useAppStore.getState>['mode'];
  kind: DimensionKind | null;
  editMode: EditMode;
};

/** @description A delayed blur from the previous selection must never edit the new selection. */
export const isStillSelected = (selectedIds: string[]): boolean => {
  const currentIds = useAppStore.getState().selectedIds;
  return (
    currentIds.length === selectedIds.length &&
    selectedIds.every((id, index) => id === currentIds[index])
  );
};

export const useSelectionDimensionUpdates = ({ appMode, kind, editMode, selectedIds }: Props) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const moveWithTemplates = useMoveWithTemplates(selectedIds);

  const updateField = useCallback(
    (field: DimensionField, newValue: number) => {
      if (!kind || kind === 'unsupported' || appMode !== 'EDITABLE') return;
      // A group's length and width are read-only
      if (editMode === 'group' && (field === 'length' || field === 'width')) return;
      if (!isStillSelected(selectedIds)) return;
      const value =
        field === 'angle'
          ? MathUtils.degToRad(clampAngle180(newValue))
          : NumUtils.mmToMetersRoundedToMm(newValue);
      const state = useFloorplanDesignStore.getState();

      // Walls (or a non-rectangular logical space) with other items can only be moved together
      if (kind === 'restricted' && field !== 'centerX' && field !== 'centerY') return;

      // Selected groups are edited through their items
      const itemIds = selectDimensionItemIds(state, selectedIds);

      if (kind === 'templates') {
        const templates = selectTemplatesByIds(state, itemIds);
        const templateItems = createItems(
          areasToFlatCollection(state.getAreasByIds(itemIds)),
          state.getObstaclesByIds(itemIds),
          state.getLogicalSpacesByIds(itemIds),
        );
        if (templateItems.length + templates.length !== itemIds.length) return;

        const templateAreas = areasToFlatCollection(
          state.getAreasByIds(templates.flatMap((template) => template.group.areaIds)),
        );
        const updated = updateTemplatesField(templateItems, templates, templateAreas, field, value);
        if (!updated) return;

        moveWithTemplates(updated.items, updated.templates);
        return;
      }

      // Any mix of areas, obstacles and logical spaces is edited the same way, whatever their types
      const items = createItems(
        areasToFlatCollection(state.getAreasByIds(itemIds)),
        state.getObstaclesByIds(itemIds),
        state.getLogicalSpacesByIds(itemIds),
      );
      if (items.length !== itemIds.length) return;

      const updatedItems = updateItemsField(
        items,
        field,
        value,
        kind === 'restricted' ? 'group' : editMode,
      );
      if (!updatedItems) return;

      // One update for all items: one generateFloorplan request and one undo step
      fddmSvc.updateItems(toItemUpdates(updatedItems));
    },
    [kind, appMode, editMode, selectedIds, fddmSvc, moveWithTemplates],
  );
  const onXChange = useCallback((value: number) => updateField('centerX', value), [updateField]);
  const onYChange = useCallback((value: number) => updateField('centerY', value), [updateField]);
  const onLengthChange = useCallback(
    (value: number) => updateField('length', value),
    [updateField],
  );
  const onWidthChange = useCallback((value: number) => updateField('width', value), [updateField]);
  const onAngleChange = useCallback((value: number) => updateField('angle', value), [updateField]);

  return { onXChange, onYChange, onLengthChange, onWidthChange, onAngleChange };
};
